import DiffMatchPatch from 'diff-match-patch';

/**
 * Helpers for QC assignment `questions[]` and `response_to_req` payloads.
 */

/**
 * @param {string} [html]
 * @returns {string}
 */
export function stripHtmlTags(html) {
  if (html == null || html === '') return '';
  return String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Character-level change percent vs original transcript (aligned with main QC `CustomTable`).
 *
 * @param {string} originalText
 * @param {string} updatedText
 * @returns {number}
 */
export function computeTranscriptChangePercentage(originalText, updatedText) {
  const oldText = String(originalText ?? '');
  const newText = String(updatedText ?? '');
  const totalOriginalChars = oldText.length;
  if (totalOriginalChars === 0) return newText.length > 0 ? 100 : 0;
  if (oldText === newText) return 0;

  try {
    const dmp = new DiffMatchPatch();
    const diff = dmp.diff_main(oldText, newText);
    dmp.diff_cleanupSemantic(diff);

    let changedChars = 0;
    diff.forEach(([op, text]) => {
      if (
        op === DiffMatchPatch.DIFF_INSERT ||
        op === DiffMatchPatch.DIFF_DELETE
      ) {
        changedChars += text.length;
      }
    });

    return (changedChars / totalOriginalChars) * 100;
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[qc] computeTranscriptChangePercentage failed', err);
    }
    return 0;
  }
}

/**
 * @param {unknown} questions
 * @returns {object[]}
 */
export function sortQuestions(questions) {
  if (!Array.isArray(questions)) return [];
  return [...questions].sort((a, b) => {
    const oa = Number(a?.property?.order ?? 0);
    const ob = Number(b?.property?.order ?? 0);
    if (oa !== ob) return oa - ob;
    return Number(a?.question_id ?? 0) - Number(b?.question_id ?? 0);
  });
}

/**
 * @param {object[]} rows
 * @returns {{ key: string, label: string, sentiment?: string }[]}
 */
export function flattenKeyedOptions(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    return Object.entries(item)
      .filter(([k]) => k !== 'sentiment' && k !== 'selected')
      .map(([key, label]) => ({
        key,
        label: String(label),
        sentiment: item.sentiment,
      }));
  });
}

/**
 * Normalize API option sentiment for UI highlighting.
 * @param {unknown} raw
 * @returns {'positive' | 'negative' | null}
 */
export function normalizeSentiment(raw) {
  if (raw == null || raw === '') return null;
  const t = String(raw).trim().toLowerCase();
  if (t === 'positive') return 'positive';
  if (t === 'negative') return 'negative';
  return null;
}

/**
 * `property.expected_answer` for UI (trimmed); empty string if absent or blank.
 * @param {object} [question]
 * @returns {string}
 */
export function getExpectedAnswerString(question) {
  const raw = question?.property?.expected_answer;
  if (raw == null) return '';
  if (Array.isArray(raw)) {
    return raw
      .map((x) => (x == null ? '' : String(x).trim()))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof raw === 'object') {
    try {
      return JSON.stringify(raw);
    } catch {
      return '';
    }
  }
  return String(raw).trim();
}

/**
 * Sentiment for the selected yes/no (or radio) label.
 * @param {{ label: string, sentiment?: string }[]} flattenedOptions
 * @param {unknown} selectedLabel
 * @returns {'positive' | 'negative' | null}
 */
export function getSentimentForYesNoSelection(flattenedOptions, selectedLabel) {
  if (selectedLabel == null || String(selectedLabel).trim() === '') return null;
  const n = String(selectedLabel).trim().toLowerCase();
  const opt = flattenedOptions.find(
    (o) => String(o.label).trim().toLowerCase() === n,
  );
  return normalizeSentiment(opt?.sentiment);
}

/**
 * Checkbox: if any selected option is negative → negative; else if any positive → positive; else null.
 * Options without sentiment are ignored for coloring.
 * @param {{ key: string, sentiment?: string }[]} flattenedOptions
 * @param {string[]} selectedKeys
 * @returns {'positive' | 'negative' | null}
 */
export function getAggregateSentimentForCheckbox(
  flattenedOptions,
  selectedKeys,
) {
  if (!Array.isArray(selectedKeys) || selectedKeys.length === 0) return null;
  const byKey = new Map(
    flattenedOptions.map((o) => [
      String(o.key),
      normalizeSentiment(o.sentiment),
    ]),
  );
  let hasNeg = false;
  let hasPos = false;
  for (const k of selectedKeys) {
    const s = byKey.get(String(k));
    if (s === 'negative') hasNeg = true;
    if (s === 'positive') hasPos = true;
  }
  if (hasNeg) return 'negative';
  if (hasPos) return 'positive';
  return null;
}

/**
 * @param {object} question
 * @returns {'yesOrNo' | 'textAnswer' | 'checkBox' | 'unknown'}
 */
export function getQuestionInputKind(question) {
  const opts = question?.options;
  if (!opts || typeof opts !== 'object') return 'unknown';
  if (Array.isArray(opts.yesOrNo) && opts.yesOrNo.length > 0) return 'yesOrNo';
  if (Array.isArray(opts.radio) && opts.radio.length > 0) return 'yesOrNo';
  if (opts.textAnswer !== undefined) return 'textAnswer';
  if (Array.isArray(opts.checkBox) && opts.checkBox.length > 0)
    return 'checkBox';
  return 'unknown';
}

/**
 * Normalize checkbox answer from API into string[] of option keys (A, B, …).
 * @param {unknown} value
 * @returns {string[]}
 */
export function normalizeCheckboxAnswer(value) {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      /* ignore */
    }
    if (value.includes(',')) {
      return value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [value.trim()].filter(Boolean);
  }
  return [];
}

/**
 * Read one question's answer from `response_to_req` data as returned by the API
 * (object, array of rows, or `{ answers: [...] }`) without flattening the whole payload.
 *
 * @param {unknown} answers
 * @param {string|number} questionId
 * @returns {unknown}
 */
export function getQuestionAnswer(answers, questionId) {
  if (answers == null) return undefined;
  const idKey = String(questionId);

  if (typeof answers === 'string') {
    try {
      return getQuestionAnswer(JSON.parse(answers), questionId);
    } catch {
      return undefined;
    }
  }

  if (Array.isArray(answers)) {
    const row = answers.find(
      (r) => String(r?.question_id ?? r?.questionId) === idKey,
    );
    if (!row) return undefined;
    const simple = row?.answer ?? row?.value ?? row?.response;
    if (simple != null) return simple;
    return extractSelectedFromOptions(row?.options);
  }

  if (typeof answers !== 'object') return undefined;

  if (answers[idKey] !== undefined) return answers[idKey];
  if (answers[questionId] !== undefined) return answers[questionId];

  if (Array.isArray(answers.answers)) {
    const row = answers.answers.find(
      (r) => String(r?.question_id ?? r?.questionId) === idKey,
    );
    return row?.answer ?? row?.value ?? row?.response;
  }

  if (Array.isArray(answers.questions)) {
    const row = answers.questions.find(
      (r) => String(r?.question_id ?? r?.questionId) === idKey,
    );
    if (!row) return undefined;
    const simple = row?.answer ?? row?.value ?? row?.response;
    if (simple != null) return simple;
    return extractSelectedFromOptions(row?.options);
  }

  return undefined;
}

function extractSelectedFromOptions(opts) {
  if (!opts || typeof opts !== 'object') return undefined;
  const rows = (Array.isArray(opts.yesOrNo) ? opts.yesOrNo : null)
    ?? (Array.isArray(opts.radio) ? opts.radio : null);
  if (rows) {
    const sel = rows.find((r) => r?.selected === true);
    if (sel) {
      const entry = Object.entries(sel).find(([k]) => k !== 'selected' && k !== 'sentiment');
      return entry ? String(entry[1]) : undefined;
    }
  }
  if (Array.isArray(opts.checkBox)) {
    const vals = opts.checkBox
      .filter((r) => r?.selected === true)
      .map((r) => Object.entries(r).find(([k]) => k !== 'selected' && k !== 'sentiment')?.[1])
      .filter((v) => v != null)
      .map(String);
    if (vals.length > 0) return vals;
  }
  if (Array.isArray(opts.textAnswer)) {
    const sel = opts.textAnswer.find((r) => r?.selected === true);
    if (sel) return sel.A ?? sel.answer ?? sel.text ?? '';
    // textAnswer is always single-valued free text (not a multi-option pick);
    // rows saved via web/'s CustomTable.jsx (pre-fix) never set `selected` —
    // fall back to the first entry instead of showing a blank field for a
    // real stored answer.
    const first = opts.textAnswer[0];
    if (first) return first.A ?? first.answer ?? first.text ?? '';
  }
  return undefined;
}

/**
 * Merge `value` for `questionId` into a copy of `response_to_req`-shaped payload.
 *
 * @param {unknown} prev
 * @param {string|number} questionId
 * @param {unknown} value
 * @returns {unknown}
 */
export function mergeAnswerIntoResponse(prev, questionId, value) {
  const idKey = String(questionId);

  if (prev == null) {
    return { [idKey]: value };
  }

  if (Array.isArray(prev)) {
    const idx = prev.findIndex(
      (r) => String(r?.question_id ?? r?.questionId) === idKey,
    );
    if (idx >= 0) {
      const next = [...prev];
      next[idx] = { ...next[idx], question_id: questionId, answer: value };
      return next;
    }
    return [...prev, { question_id: questionId, answer: value }];
  }

  if (typeof prev === 'object') {
    if (Array.isArray(prev.answers)) {
      const row = prev.answers.find(
        (r) => String(r?.question_id ?? r?.questionId) === idKey,
      );
      const nextAnswers = row
        ? prev.answers.map((r) =>
            String(r?.question_id ?? r?.questionId) === idKey
              ? { ...r, answer: value }
              : r,
          )
        : [...prev.answers, { question_id: questionId, answer: value }];
      return { ...prev, answers: nextAnswers };
    }
    return { ...prev, [idKey]: value };
  }

  return { [idKey]: value };
}

/**
 * @param {object} task
 * @returns {boolean}
 */
export function isAssessmentReadOnly(task) {
  if (!task) return false;
  const st =
    task.req_status != null ? String(task.req_status).toUpperCase() : '';
  if (
    st === 'SUBMITTED' ||
    st === 'IN-REVIEW' ||
    st === 'APPROVED' ||
    st === 'REJECTED'
  ) {
    return true;
  }
  // 'Save for later' reuses the submit-response upsert, which stamps
  // response_submitted_at even though req_status stays IN-PROGRESS —
  // don't let that timestamp force read-only for an in-progress draft.
  if (st === 'IN-PROGRESS' || st === 'OPEN') return false;
  if (task.response_submitted_at) return true;
  return false;
}

/**
 * Whether the modal has any content worth saving as a draft (does not require required fields).
 *
 * @param {{ answers?: object, updatedTranscript?: string }} snapshot
 * @param {{ sortedQuestions?: object[], classificationTags?: string[], showTranscriptFields?: boolean }} ctx
 * @returns {boolean}
 */
export function hasAtLeastOneDraftProgress(snapshot, ctx) {
  const sortedQuestions = Array.isArray(ctx?.sortedQuestions)
    ? ctx.sortedQuestions
    : [];
  const answers = snapshot?.answers;
  for (let idx = 0; idx < sortedQuestions.length; idx++) {
    const q = sortedQuestions[idx];
    const qid =
      q?.question_id === undefined || q?.question_id === null
        ? idx
        : q.question_id;
    const idKey = String(qid);
    const val = answers?.[idKey];
    const kind = getQuestionInputKind(q);
    if (kind === 'checkBox') {
      if (Array.isArray(val) && val.length > 0) return true;
    } else if (kind === 'yesOrNo' || kind === 'textAnswer') {
      if (String(val ?? '').trim() !== '') return true;
    }
  }

  if (ctx?.showTranscriptFields) {
    const ut = snapshot?.updatedTranscript;
    if (typeof ut === 'string' && ut.trim() !== '') return true;
  }

  const tags = ctx?.classificationTags;
  if (Array.isArray(tags) && tags.some((t) => String(t).trim() !== ''))
    return true;

  return false;
}

/**
 * `reqStatus` query param for final submit (Submit and Next / Submit and Close).
 * No assigned reviewer (auth_user_id missing) → SUBMITTED; reviewer assigned → IN-REVIEW.
 *
 * @param {object|null|undefined} task
 * @returns {'IN-REVIEW' | 'SUBMITTED'}
 */
export function getFinalSubmitReqStatus(task) {
  const reviewer = task?.assigned_reviewer;
  if (!reviewer || typeof reviewer !== 'object') return 'SUBMITTED';
  const authId =
    reviewer.auth_user_id ??
    reviewer.authUserId ??
    reviewer.auth_id ??
    reviewer.authId ??
    reviewer.id;
  if (authId == null || authId === '') return 'SUBMITTED';
  if (String(authId).trim() === '') return 'SUBMITTED';
  return 'IN-REVIEW';
}

/**
 * @param {object} row
 * @returns {string}
 */
export function getWorkflowTagNameFromRow(row) {
  if (!row || typeof row !== 'object') return '';
  const name =
    row.tag_name ?? row.tagName ?? row.Tag_Name ?? row.name ?? row.label;
  if (name != null && String(name).trim() !== '') return String(name);
  return '';
}

/**
 * Unique sorted tag names for a workflow (for classification dropdown options).
 *
 * @param {object[]} tagRows
 * @returns {string[]}
 */
export function buildClassificationTagNamesForWorkflow(tagRows) {
  if (!Array.isArray(tagRows)) return [];
  const set = new Set();
  for (const row of tagRows) {
    const n = getWorkflowTagNameFromRow(row);
    if (n) set.add(n);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

/**
 * Flatten `tagsByWorkflowId` into unique rows (by `tag_id`) for “unknown workflow” fallback.
 *
 * @param {Record<string, object[]>|undefined|null} tagsByWorkflowId
 * @returns {object[]}
 */
export function mergeAllWorkflowTagRowsUnique(tagsByWorkflowId) {
  const map = new Map();
  Object.values(tagsByWorkflowId ?? {}).forEach((list) => {
    if (!Array.isArray(list)) return;
    list.forEach((row) => {
      if (row && row.tag_id !== undefined) {
        map.set(row.tag_id, row);
      }
    });
  });
  return [...map.values()];
}

/**
 * Map saved tag strings / ids to canonical `tag_name` values from workflow tag rows
 * (matches {@link buildClassificationTagNamesForWorkflow} / dropdown options).
 *
 * @param {string[]} names
 * @param {object[]} tagRows
 * @returns {string[]}
 */
export function normalizeClassificationTagNamesToWorkflow(names, tagRows) {
  const rows = Array.isArray(tagRows) ? tagRows : [];
  if (rows.length === 0) {
    return Array.isArray(names)
      ? names.map((t) => String(t).trim()).filter(Boolean)
      : [];
  }

  const byId = new Map();
  const canonicalByLower = new Map();
  for (const r of rows) {
    const name = getWorkflowTagNameFromRow(r);
    if (name) canonicalByLower.set(name.toLowerCase(), name);
    const id = r.tag_id ?? r.tagId;
    if (id !== undefined && id !== null) {
      byId.set(String(id), name);
    }
  }

  return (Array.isArray(names) ? names : [])
    .map((raw) => {
      const t = String(raw).trim();
      if (!t) return '';
      if (canonicalByLower.has(t.toLowerCase())) {
        return canonicalByLower.get(t.toLowerCase());
      }
      if (byId.has(t)) {
        return byId.get(t) ?? t;
      }
      return t;
    })
    .filter(Boolean);
}

/**
 * First array-shaped classification list on `data` (or nested `response`), if any.
 *
 * @param {object} data
 * @returns {unknown[]|undefined}
 */
function classificationArrayFromResponseShape(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data))
    return undefined;
  const nested =
    data.response && typeof data.response === 'object' ? data.response : null;
  const candidates = [
    data.classificationTags,
    data.classification_tags,
    nested?.classificationTags,
    nested?.classification_tags,
  ];
  const found = candidates.find((a) => Array.isArray(a));
  return found;
}

/**
 * Read stored classification as tag names. Prefers `classificationTags` / `classification_tags`;
 * migrates legacy `classificationTag` (name or tag_id string) using `tagRows`.
 * Names are normalized to match workflow API tag strings for chips + dropdown.
 *
 * @param {object} data
 * @param {object[]} tagRows
 * @returns {string[]}
 */
export function parseClassificationTagsFromResponse(data, tagRows) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
  const rows = Array.isArray(tagRows) ? tagRows : [];

  const arr = classificationArrayFromResponseShape(data);
  if (arr !== undefined) {
    const names = arr.map((t) => String(t).trim()).filter(Boolean);
    return normalizeClassificationTagNamesToWorkflow(names, rows);
  }

  const legNested =
    data.response &&
    typeof data.response === 'object' &&
    typeof data.response.classificationTag === 'string'
      ? data.response.classificationTag
      : null;
  const leg = legNested ?? data.classificationTag;
  if (typeof leg !== 'string' || leg === '') return [];

  const namesFromRows = rows.map(getWorkflowTagNameFromRow).filter(Boolean);
  if (namesFromRows.includes(leg)) {
    return normalizeClassificationTagNamesToWorkflow([leg], rows);
  }
  const row = rows.find((r) => String(r.tag_id ?? r.tagId) === leg);
  const mapped = getWorkflowTagNameFromRow(row);
  if (mapped) return normalizeClassificationTagNamesToWorkflow([mapped], rows);
  return normalizeClassificationTagNamesToWorkflow([leg], rows);
}

/**
 * Merge `classificationTags` (tag names only) into a `response_to_req`-shaped object.
 * Removes legacy `classificationTag` when present.
 *
 * @param {unknown} prev
 * @param {string[]} classificationTags
 * @returns {unknown}
 */
export function mergeClassificationTagsIntoResponse(prev, classificationTags) {
  const tags = Array.isArray(classificationTags)
    ? classificationTags.map((t) => String(t).trim()).filter(Boolean)
    : [];

  if (prev == null) return { classificationTags: tags };

  if (Array.isArray(prev)) return prev;

  if (typeof prev === 'object') {
    const next = { ...prev, classificationTags: tags };
    if ('classificationTag' in next) delete next.classificationTag;
    return next;
  }

  return { classificationTags: tags };
}

/**
 * Merge `updatedTranscript` into a `response_to_req`-shaped object.
 *
 * @param {unknown} prev
 * @param {string} updatedTranscript
 * @returns {unknown}
 */
export function mergeUpdatedTranscriptIntoResponse(prev, updatedTranscript) {
  const text = typeof updatedTranscript === 'string' ? updatedTranscript : '';
  if (prev == null) return { updatedTranscript: text };
  if (Array.isArray(prev)) return prev;
  if (typeof prev === 'object') {
    return { ...prev, updatedTranscript: text };
  }
  return { updatedTranscript: text };
}

/**
 * Question ids (same rules as `DynamicAssessmentQuestions` / `buildQuestionsPayload`) whose
 * `property.link_transcript === true` should mirror **Updated Transcript** two-way.
 *
 * @param {object[]} sortedQuestionsList - e.g. `sortQuestions(task.questions)`
 * @returns {(string|number)[]}
 */
export function getLinkedTranscriptQuestionIds(sortedQuestionsList) {
  if (!Array.isArray(sortedQuestionsList)) return [];
  const ids = [];
  sortedQuestionsList.forEach((q, idx) => {
    if (q?.property?.link_transcript !== true) return;
    if (getQuestionInputKind(q) !== 'textAnswer') return;
    const qid =
      q?.question_id === undefined || q?.question_id === null
        ? idx
        : q.question_id;
    ids.push(qid);
  });
  return ids;
}

/**
 * Set `updatedTranscript` and every linked transcript question answer to the same string.
 *
 * @param {unknown} prev
 * @param {string} transcriptText
 * @param {(string|number)[]} linkedQuestionIds
 * @param {string} [linkedTranscriptText]
 * @returns {unknown}
 */
export function mergeTranscriptAndLinkedQuestions(
  prev,
  transcriptText,
  linkedQuestionIds,
  linkedTranscriptText,
) {
  const text = typeof transcriptText === 'string' ? transcriptText : '';
  const linkedText =
    typeof linkedTranscriptText === 'string' ? linkedTranscriptText : text;
  let next = mergeUpdatedTranscriptIntoResponse(prev, text);
  if (!Array.isArray(linkedQuestionIds)) return next;
  for (const qid of linkedQuestionIds) {
    next = mergeAnswerIntoResponse(next, qid, linkedText);
  }
  return next;
}

function normalizeSelectedStrings(answer) {
  if (Array.isArray(answer)) {
    return answer.map((v) => String(v).trim().toLowerCase()).filter(Boolean);
  }
  if (answer == null) return [];
  return [String(answer).trim().toLowerCase()].filter(Boolean);
}

function withSelectedFlag(optionRows, answer) {
  if (!Array.isArray(optionRows)) return optionRows;
  const selectedValues = normalizeSelectedStrings(answer);
  return optionRows.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const next = { ...row };
    if ('selected' in next) delete next.selected;
    const entries = Object.entries(next).filter(
      ([k]) => k !== 'sentiment' && k !== 'selected',
    );
    const isSelected = entries.some(([key, label]) => {
      const k = String(key).trim().toLowerCase();
      const l = String(label).trim().toLowerCase();
      return selectedValues.includes(k) || selectedValues.includes(l);
    });
    if (isSelected) next.selected = true;
    return next;
  });
}

/**
 * Build API payload questions by marking selected options from current answers.
 *
 * @param {object[]} questions
 * @param {unknown} answers
 * @returns {object[]}
 */
export function buildQuestionsPayload(questions, answers) {
  if (!Array.isArray(questions)) return [];
  return questions.map((q, idx) => {
    const qid =
      q?.question_id === undefined || q?.question_id === null
        ? idx
        : q.question_id;
    const selectedAnswer = getQuestionAnswer(answers, qid);
    const opts = q?.options ?? {};
    const nextOptions = { ...opts };
    if (Array.isArray(opts.yesOrNo)) {
      nextOptions.yesOrNo = withSelectedFlag(opts.yesOrNo, selectedAnswer);
    }
    if (Array.isArray(opts.radio)) {
      nextOptions.radio = withSelectedFlag(opts.radio, selectedAnswer);
    }
    if (Array.isArray(opts.checkBox)) {
      nextOptions.checkBox = withSelectedFlag(opts.checkBox, selectedAnswer);
    }
    if (opts.textAnswer !== undefined && selectedAnswer != null) {
      if (
        typeof selectedAnswer === 'string' ||
        typeof selectedAnswer === 'number' ||
        typeof selectedAnswer === 'boolean'
      ) {
        // Match main QC platform payload: array of rows with `A` + `selected`, not a plain string.
        nextOptions.textAnswer = [
          { A: String(selectedAnswer), selected: true },
        ];
      }
    }
    return { ...q, options: nextOptions };
  });
}

/**
 * @param {object|null} root
 * @returns {object|null}
 */
export function getResponseToReqEffectiveBase(root) {
  if (!root || typeof root !== 'object') return null;
  const nested = root.response;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested;
  }
  return root;
}

/**
 * @param {object|null} base
 * @returns {{ changePercentage: unknown, updatedVendorTranscript: string, hasChangePercentageKey: boolean, hasUpdatedVendorTranscriptKey: boolean }}
 */
export function getResponseToReqSubmissionMeta(base) {
  if (!base || typeof base !== 'object') {
    return {
      changePercentage: null,
      updatedVendorTranscript: '',
      hasChangePercentageKey: false,
      hasUpdatedVendorTranscriptKey: false,
    };
  }
  const hasCp = Object.prototype.hasOwnProperty.call(base, 'change_percentage');
  const hasUvt = Object.prototype.hasOwnProperty.call(
    base,
    'updated_vendor_transcript',
  );
  const cp = hasCp ? base.change_percentage : null;
  let transcriptStr = '';
  if (hasUvt) {
    const v = base.updated_vendor_transcript;
    transcriptStr = typeof v === 'string' ? v : v != null ? String(v) : '';
  }
  return {
    changePercentage: cp,
    updatedVendorTranscript: transcriptStr,
    hasChangePercentageKey: hasCp,
    hasUpdatedVendorTranscriptKey: hasUvt,
  };
}

